import React from 'react';
import { Button, Typography } from '@mui/material';
import { BaseDialog } from './BaseDialog.tsx';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Для деструктивних дій (видалення тощо) - точні кольори з макета (Figma "Видалити задачу?"):
   *  підтвердження - суцільний #FF7070, скасування - контур #6EB52C. Не семантичні MUI-кольори
   *  (success/error), бо вони не збігаються з брендовими значеннями макета. */
  danger?: boolean;
}

const dangerButtonSx = {
  height: 40,
  px: 2,
  borderRadius: '6px',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  textTransform: 'none' as const,
  boxShadow: 'none',
};

/**
 * Діалог для підтвердження дії
 * @param open - чи відкрите вікно
 * @param onClose - функція для закриття вікна (при кліці на "Скасувати" або хрестик)
 * @param onConfirm - функція, що виконується при підтвердженні
 * @param title - заголовок вікна
 * @param message - текст-повідомлення
 * @param confirmText - текст для кнопки підтвердження (за замовчуванням "Підтвердити")
 * @param cancelText - текст для кнопки скасування (за замовчуванням "Скасувати")
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Підтвердити',
  cancelText = 'Скасувати',
  danger,
}) => {
  const actions = danger ? (
    <>
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{ ...dangerButtonSx, bgcolor: '#FFFFFF', borderColor: '#6EB52C', color: '#6EB52C', '&:hover': { borderColor: '#6EB52C', bgcolor: 'rgba(110, 181, 44, 0.08)' } }}
      >
        {cancelText}
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        sx={{ ...dangerButtonSx, bgcolor: '#FF7070', color: '#FFFFFF', '&:hover': { bgcolor: '#FF7070' } }}
      >
        {confirmText}
      </Button>
    </>
  ) : (
    <>
      <Button onClick={onClose} color="secondary">
        {cancelText}
      </Button>
      <Button onClick={onConfirm} color="primary" variant="contained">
        {confirmText}
      </Button>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={title}
      actions={actions}
      maxWidth={danger ? false : undefined}
      paperSx={danger ? { width: 400, borderRadius: '16px' } : undefined}
      titleSx={danger ? { fontSize: 20, lineHeight: '23px', fontWeight: 600, color: '#26313F' } : undefined}
      contentSx={danger ? { p: 3, borderBottom: 'none' } : undefined}
    >
      <Typography sx={danger ? { fontSize: 16, lineHeight: '24px', color: '#26313F' } : undefined}>
        {message}
      </Typography>
    </BaseDialog>
  );
};
